import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';

const SORT_OPTIONS = [
  { value: 'recent', label: 'Más recientes' },
  { value: 'oldest', label: 'Más antiguos' },
  { value: 'brand_az', label: 'Marca A→Z' },
  { value: 'model_az', label: 'Modelo A→Z' },
];

const LS_KEY = 'last_selected_reception_id';

const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/D';
  try {
    return new Date(dateStr)?.toLocaleString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

const mapWorkOrderToUnit = (wo) => ({
  receptionId: wo?.id,
  folioOT: wo?.folio_ot || '',
  estadoOT: wo?.estado_ot || '',
  fechaIngreso: wo?.fecha_ingreso,
  year: wo?.vehicle?.year || '',
  make: wo?.vehicle?.marca || '',
  model: wo?.vehicle?.modelo || '',
  vin: wo?.vehicle?.vin || '',
  plates: wo?.vehicle?.placas || '',
  color: wo?.vehicle?.color || '',
  motor: wo?.vehicle?.motor || '',
  combustible: wo?.vehicle?.combustible || '',
  kilometraje: wo?.kilometraje || '',
  vehicleId: wo?.vehicle?.id || null,
  clientName: wo?.client?.nombre || '',
  clientId: wo?.client?.id || null,
  rawWorkOrder: wo,
});

const mapReceptionToUnit = (rec) => ({
  receptionId: rec?.id,
  folioOT: `REC-${String(rec?.id)?.slice(-6)?.toUpperCase()}`,
  estadoOT: rec?.status || 'received',
  fechaIngreso: rec?.created_at,
  year: rec?.vehicles?.year || rec?.vehicle?.year || '',
  make: rec?.vehicles?.marca || rec?.vehicle?.marca || '',
  model: rec?.vehicles?.modelo || rec?.vehicle?.modelo || '',
  vin: rec?.vehicles?.vin || rec?.vehicle?.vin || '',
  plates: rec?.vehicles?.placas || rec?.vehicle?.placas || '',
  color: rec?.vehicles?.color || rec?.vehicle?.color || '',
  motor: rec?.vehicles?.motor || rec?.vehicle?.motor || '',
  combustible: rec?.vehicles?.combustible || rec?.vehicle?.combustible || '',
  kilometraje: rec?.odometer || '',
  vehicleId: rec?.vehicles?.id || rec?.vehicle?.id || null,
  clientName: rec?.clients?.nombre || rec?.client?.nombre || '',
  clientId: rec?.clients?.id || rec?.client?.id || null,
  isReceptionFlow: true,
  rawReception: rec,
});

const InspectionVehicleSelector = ({ onVehicleSelected, selectedReceptionId, refreshTrigger }) => {
  const { userProfile } = useAuth();
  const [viewMode, setViewMode] = useState('recent');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [units, setUnits] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);
  const autoSelectAttemptedRef = useRef(false);
  const abortRef = useRef(null);
  const PAGE_SIZE = 50;

  const fetchUnits = useCallback(async (query, sort, pageNum, append = false) => {
    if (!userProfile?.company_id) return;

    // Cancel any in-flight request
    if (abortRef?.current) {
      abortRef?.current?.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);
    try {
      const limit = viewMode === 'recent' ? 20 : PAGE_SIZE;
      const offset = pageNum * limit;

      let q = supabase
        ?.from('receptions')
        ?.select(
          `id,
          created_at,
          odometer,
          motivo_visita,
          company_id,
          vehicle_id,
          client_id,
          vehicles!receptions_vehicle_id_fkey(id, vin, marca, modelo, year, motor, combustible, placas, color, tipo),
          clients!receptions_client_id_fkey(id, nombre, telefono, email)`,
          { count: 'exact' }
        )
        ?.eq('company_id', userProfile?.company_id)
        ?.abortSignal(controller?.signal);

      switch (sort) {
        case 'oldest':
          q = q?.order('created_at', { ascending: true });
          break;
        default:
          q = q?.order('created_at', { ascending: false });
      }

      if (viewMode === 'recent') {
        q = q?.limit(20);
      } else {
        q = q?.range(offset, offset + PAGE_SIZE - 1);
      }

      const { data, error: fetchError, count } = await q;

      if (controller?.signal?.aborted) return;

      if (fetchError) {
        console.error('[InspectionVehicleSelector] Fetch error:', fetchError);
        setError('Error al cargar unidades');
        return;
      }

      let results = (data || [])?.map(mapReceptionToUnit);

      if (query?.trim()) {
        const q2 = query?.trim()?.toLowerCase();
        results = results?.filter(r =>
          r?.make?.toLowerCase()?.includes(q2) ||
          r?.model?.toLowerCase()?.includes(q2) ||
          r?.vin?.toLowerCase()?.includes(q2) ||
          r?.folioOT?.toLowerCase()?.includes(q2) ||
          r?.clientName?.toLowerCase()?.includes(q2)
        );
      }

      if (sort === 'brand_az') {
        results?.sort((a, b) => a?.make?.localeCompare(b?.make, 'es'));
      } else if (sort === 'model_az') {
        results?.sort((a, b) => a?.model?.localeCompare(b?.model, 'es'));
      }

      setTotalCount(count || results?.length);
      setHasMore(viewMode === 'all' && (offset + PAGE_SIZE) < (count || 0));

      if (append) {
        setUnits(prev => [...prev, ...results]);
      } else {
        setUnits(results);
      }
    } catch (err) {
      if (err?.name === 'AbortError' || controller?.signal?.aborted) return;
      console.error('[InspectionVehicleSelector] Error:', err);
      setError('Error inesperado al cargar unidades');
    } finally {
      if (!controller?.signal?.aborted) {
        setIsLoading(false);
      }
    }
  }, [userProfile?.company_id, viewMode]);

  // Fetch a specific reception by ID (direct lookup — primary path)
  const fetchSpecificWorkOrder = useCallback(async (receptionId) => {
    if (!receptionId || !userProfile?.company_id) return null;
    // Now queries receptions table (renamed for backward compat)
    return fetchSpecificReception(receptionId);
  }, [userProfile?.company_id]);

  // Fetch a specific reception by ID (for auto-select when coming from Reception page)
  const fetchSpecificReception = useCallback(async (receptionId) => {
    if (!receptionId) return null;
    try {
      const { data, error: fetchError } = await supabase
        ?.from('receptions')
        ?.select(
          `id,
          status,
          odometer,
          motivo_visita,
          created_at,
          company_id,
          vehicle_id,
          client_id,
          vehicles!receptions_vehicle_id_fkey(id, marca, modelo, vin, placas, color, tipo, year, motor, combustible),
          clients!receptions_client_id_fkey(id, nombre, telefono, email)`
        )
        ?.eq('id', receptionId)
        ?.maybeSingle();

      if (fetchError || !data) {
        console.error('[InspectionVehicleSelector] Reception fetch error:', fetchError);
        return null;
      }
      return mapReceptionToUnit(data);
    } catch (err) {
      console.error('[InspectionVehicleSelector] fetchSpecificReception exception:', err);
      return null;
    }
  }, []);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortRef?.current) abortRef?.current?.abort();
    };
  }, []);

  // Initial load — reset auto-select attempt flag on refresh
  useEffect(() => {
    autoSelectAttemptedRef.current = false;
    fetchUnits(searchQuery, sortBy, 0, false);
  }, [viewMode, sortBy, userProfile?.company_id, refreshTrigger]);

  // Debounced search
  useEffect(() => {
    if (debounceRef?.current) clearTimeout(debounceRef?.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      fetchUnits(searchQuery, sortBy, 0, false);
    }, 300);
    return () => clearTimeout(debounceRef?.current);
  }, [searchQuery]);

  // Auto-select: try list first, then direct fetch if not found
  useEffect(() => {
    if (units?.length === 0 || isLoading) return;
    if (autoSelectAttemptedRef?.current) return;

    // Priority 1: selectedReceptionId prop (from navigation/Reception)
    if (selectedReceptionId) {
      const found = units?.find(u => u?.receptionId === selectedReceptionId);
      if (found) {
        autoSelectAttemptedRef.current = true;
        onVehicleSelected?.(found);
        return;
      }
      // Not found in work_orders list — try receptions table directly
      autoSelectAttemptedRef.current = true;
      fetchSpecificReception(selectedReceptionId)?.then(unit => {
        if (unit) {
          setUnits(prev => {
            const alreadyIn = prev?.some(u => u?.receptionId === unit?.receptionId);
            return alreadyIn ? prev : [unit, ...prev];
          });
          onVehicleSelected?.(unit);
          return;
        }
        // Fallback: try work_orders
        fetchSpecificWorkOrder(selectedReceptionId)?.then(woUnit => {
          if (woUnit) {
            setUnits(prev => {
              const alreadyIn = prev?.some(u => u?.receptionId === woUnit?.receptionId);
              return alreadyIn ? prev : [woUnit, ...prev];
            });
            onVehicleSelected?.(woUnit);
          }
        });
      });
      return;
    }

    // Priority 2: localStorage last selected
    const lastId = localStorage.getItem(LS_KEY);
    if (lastId) {
      const found = units?.find(u => u?.receptionId === lastId);
      if (found) {
        autoSelectAttemptedRef.current = true;
        onVehicleSelected?.(found);
      }
    }
  }, [units, selectedReceptionId, isLoading]);

  const handleSelectUnit = (unit) => {
    localStorage.setItem(LS_KEY, unit?.receptionId);
    onVehicleSelected?.(unit);
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchUnits(searchQuery, sortBy, nextPage, true);
  };

  const handleToggleView = () => {
    setViewMode(prev => prev === 'recent' ? 'all' : 'recent');
    setPage(0);
    setSearchQuery('');
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-2">
      {/* Top bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 border-b border-border">
        {/* Left: view mode selector */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="relative">
            <select
              value={viewMode}
              onChange={e => { setViewMode(e?.target?.value); setPage(0); }}
              className="appearance-none pl-3 pr-8 py-1.5 text-sm rounded-md border border-border bg-muted text-foreground font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="recent">Recepciones Recientes (20)</option>
              <option value="all">Todas las unidades</option>
            </select>
            <Icon name="ChevronDown" size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
        </div>

        {/* Center: search */}
        <div className="flex-1 relative">
          <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            ref={searchRef}
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e?.target?.value)}
            placeholder="Buscar por Marca, Modelo o VIN"
            className="w-full pl-9 pr-4 py-1.5 text-sm rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={13} />
            </button>
          )}
        </div>

        {/* Right: sort + toggle */}
        <div className="flex items-center gap-2 shrink-0">
          <div className="relative">
            <select
              value={sortBy}
              onChange={e => { setSortBy(e?.target?.value); setPage(0); }}
              className="appearance-none pl-3 pr-7 py-1.5 text-xs rounded-md border border-border bg-muted text-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {SORT_OPTIONS?.map(o => (
                <option key={o?.value} value={o?.value}>{o?.label}</option>
              ))}
            </select>
            <Icon name="ChevronsUpDown" size={12} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
          <button
            type="button"
            onClick={handleToggleView}
            className={`px-3 py-1.5 text-xs rounded-md border font-medium transition-colors ${
              viewMode === 'all' ?'bg-primary text-primary-foreground border-primary' :'bg-muted text-foreground border-border hover:bg-muted/80'
            }`}
          >
            {viewMode === 'all' ? 'Recientes' : 'Ver todos'}
          </button>
        </div>
      </div>
      {/* List */}
      <div className="max-h-72 overflow-y-auto">
        {/* Loading */}
        {isLoading && units?.length === 0 && (
          <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Cargando unidades...</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 px-4 py-4 text-sm text-destructive">
            <Icon name="AlertCircle" size={15} />
            <span>{error}</span>
            <button
              type="button"
              onClick={() => fetchUnits(searchQuery, sortBy, 0, false)}
              className="ml-auto text-xs underline"
            >
              Reintentar
            </button>
          </div>
        )}

        {/* Empty */}
        {!isLoading && !error && units?.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <Icon name="Car" size={28} className="mb-2 opacity-30" />
            <p className="text-sm">
              {searchQuery ? 'Sin resultados para la búsqueda' : 'No hay unidades registradas'}
            </p>
          </div>
        )}

        {/* Rows */}
        {units?.map(unit => {
          const isSelected = unit?.receptionId === selectedReceptionId;
          return (
            <button
              key={unit?.receptionId}
              type="button"
              onClick={() => handleSelectUnit(unit)}
              className={`w-full flex items-center gap-3 px-4 py-3 border-b border-border/50 last:border-0 text-left transition-colors ${
                isSelected
                  ? 'bg-primary/8 border-l-2 border-l-primary' :'hover:bg-muted/50'
              }`}
            >
              {/* Vehicle icon */}
              <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                isSelected ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
              }`}>
                <Icon name="Car" size={15} />
              </div>
              {/* Main info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-foreground">
                    {unit?.make || 'Sin marca'} {unit?.model || ''}
                    {unit?.year ? ` ${unit?.year}` : ''}
                  </span>
                  {isSelected && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-primary/15 text-primary font-medium">
                      Seleccionada
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                  {unit?.vin && (
                    <span className="text-xs text-muted-foreground font-mono">
                      VIN: {unit?.vin}
                    </span>
                  )}
                  {unit?.plates && (
                    <span className="text-xs text-muted-foreground font-mono">
                      Placas: {unit?.plates}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    <Icon name="Clock" size={10} className="inline mr-0.5" />
                    {formatDateTime(unit?.fechaIngreso)}
                  </span>
                  {unit?.clientName && (
                    <span className="text-xs text-muted-foreground">
                      <Icon name="User" size={10} className="inline mr-0.5" />
                      {unit?.clientName}
                    </span>
                  )}
                </div>
              </div>
              {/* Folio + select indicator */}
              <div className="shrink-0 flex flex-col items-end gap-1">
                <span className="text-xs font-mono text-muted-foreground">{unit?.folioOT}</span>
                {isSelected
                  ? <Icon name="CheckCircle2" size={15} className="text-primary" />
                  : <Icon name="ChevronRight" size={14} className="text-muted-foreground" />
                }
              </div>
            </button>
          );
        })}

        {/* Load more (all mode) */}
        {viewMode === 'all' && hasMore && (
          <div className="px-4 py-3 flex justify-center">
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={isLoading}
              className="text-sm text-primary hover:underline disabled:opacity-50 flex items-center gap-1"
            >
              {isLoading ? (
                <><div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" /> Cargando...</>
              ) : (
                <>Cargar más <Icon name="ChevronDown" size={13} /></>
              )}
            </button>
          </div>
        )}
      </div>
      {/* Footer info */}
      {!isLoading && units?.length > 0 && (
        <div className="px-4 py-2 border-t border-border/50 bg-muted/20">
          <p className="text-xs text-muted-foreground">
            {viewMode === 'recent'
              ? `Mostrando ${units?.length} unidades recientes`
              : `Mostrando ${units?.length} de ${totalCount} unidades`
            }
            {selectedReceptionId && (
              <span className="ml-2 text-primary font-medium">
                · Unidad seleccionada
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default InspectionVehicleSelector;
